import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "محول قياسات الطبخ - تحويل الكوب والملعقة والغرام والأوقية";
const DESCRIPTION =
  "محول قياسات الطبخ المجاني - حوّل بين الكوب والملعقة الكبيرة والصغيرة والغرام والأوقية والمل بدقة. مثالي للطبخ والخبيز وتحضير الوصفات. احصل على النتيجة فوراً.";
const KEYWORDS =
  "محول قياسات الطبخ, تحويل وحدات الطبخ, كم ملعقة في كوب, تحويل الغرام إلى كوب, تحويل الأوقية إلى مل, وحدات قياس المطبخ, cooking measurement converter, تحويل وصفات الطبخ";
const OG_URL = "https://do-calculate.com/calculator/ar/cooking-measurement/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "محول قياسات الطبخ",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "محول قياسات الطبخ",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كم ملعقة كبيرة في كوب واحد؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يحتوي الكوب الواحد على 16 ملعقة كبيرة (طعام). أي أن ربع كوب يساوي 4 ملاعق كبيرة، ونصف كوب يساوي 8 ملاعق كبيرة.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحول الأكواب إلى مل؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "الكوب الأمريكي القياسي يساوي 236.588 مل تقريباً. لتحويل الأكواب إلى مل، اضرب عدد الأكواب في 236.588. مثلاً: كوبان = 473.176 مل.",
        },
      },
      {
        "@type": "Question",
        name: "ما الفرق بين الأونصة السائلة والأونصة الوزنية؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "الأونصة السائلة (fl oz) وحدة حجم تساوي 29.5735 مل، بينما الأونصة الوزنية (oz) وحدة كتلة تساوي 28.3495 جرام. الأولى تُستخدم للسوائل والثانية للمواد الجافة.",
        },
      },
    ],
  },
];

// Conversion factors to milliliters (ml) for volume units
const VOLUME_UNITS = {
  ml: { factor: 1, name: "مل (ml)" },
  tsp: { factor: 4.92892, name: "ملعقة صغيرة (tsp)" },
  tbsp: { factor: 14.7868, name: "ملعقة كبيرة (tbsp)" },
  fl_oz: { factor: 29.5735, name: "أونصة سائلة (fl oz)" },
  cup: { factor: 236.588, name: "كوب أمريكي (cup)" },
  pint: { factor: 473.176, name: "باينت (pint)" },
  quart: { factor: 946.353, name: "كوارت (quart)" },
  gallon: { factor: 3785.41, name: "جالون (gallon)" },
  liter: { factor: 1000, name: "لتر (L)" },
};

// Conversion factors to grams for weight units
const WEIGHT_UNITS = {
  g: { factor: 1, name: "جرام (g)" },
  kg: { factor: 1000, name: "كيلوجرام (kg)" },
  oz: { factor: 28.3495, name: "أونصة (oz)" },
  lb: { factor: 453.592, name: "رطل (lb)" },
};

// Common ingredients with density (g/ml) for volume ↔ weight conversion
const INGREDIENTS = {
  water: { name: "ماء", density: 1.0 },
  milk: { name: "حليب", density: 1.03 },
  flour_ap: { name: "دقيق أبيض (للجميع)", density: 0.529 },
  flour_bread: { name: "دقيق الخبز", density: 0.529 },
  flour_cake: { name: "دقيق الكيك", density: 0.5 },
  sugar_white: { name: "سكر أبيض (حبيبي)", density: 0.845 },
  sugar_brown: { name: "سكر بني (مكبوس)", density: 0.882 },
  sugar_powder: { name: "سكر ناعم (بودرة)", density: 0.56 },
  butter: { name: "زبدة", density: 0.911 },
  oil_veg: { name: "زيت نباتي", density: 0.917 },
  oil_olive: { name: "زيت زيتون", density: 0.914 },
  honey: { name: "عسل", density: 1.42 },
  salt: { name: "ملح", density: 1.217 },
  baking_soda: { name: "بيكنج صودا", density: 1.0 },
  baking_powder: { name: "باودر الخبز", density: 0.9 },
  cocoa: { name: "كاكاو", density: 0.529 },
  rice: { name: "أرز نيئ", density: 0.751 },
  oats: { name: "شوفان", density: 0.4 },
  cornstarch: { name: "نشا الذرة", density: 0.608 },
  cream: { name: "كريمة الطبخ", density: 0.994 },
  yogurt: { name: "لبن زبادي", density: 1.03 },
  syrup: { name: "شراب / ديبس", density: 1.33 },
  vinegar: { name: "خل", density: 1.006 },
};

const CONVERSION_TYPES = {
  volume_to_volume: "تحويل حجم إلى حجم",
  weight_to_weight: "تحويل وزن إلى وزن",
  volume_to_weight: "تحويل حجم إلى وزن",
  weight_to_volume: "تحويل وزن إلى حجم",
};

function CookingMeasurementCalculator() {
  const [conversionType, setConversionType] = useState("volume_to_volume");
  const [fromUnit, setFromUnit] = useState("cup");
  const [toUnit, setToUnit] = useState("ml");
  const [ingredient, setIngredient] = useState("flour_ap");
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateResult = () => {
    setError("");
    setResult(null);

    const val = parseFloat(inputValue);
    if (!inputValue || isNaN(val) || val <= 0) {
      if (inputValue) {
        setError("يرجى إدخال قيمة صحيحة وموجبة");
      }
      return;
    }

    try {
      let convertedValue = 0;
      let formulaText = "";
      let fromUnitName = "";
      let toUnitName = "";
      let additionalInfo = [];

      if (conversionType === "volume_to_volume") {
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} مل/وحدة ÷ ${toFactor} مل/وحدة = ${convertedValue.toFixed(4)} ${toUnitName}`;

        // Add helpful equivalents
        const inMlFinal = val * fromFactor;
        additionalInfo.push({ label: "بالمل", value: `${inMlFinal.toFixed(2)} ml` });
        if (fromUnit !== "tsp" && toUnit !== "tsp") additionalInfo.push({ label: "بالملاعق الصغيرة", value: `${(inMlFinal / VOLUME_UNITS.tsp.factor).toFixed(2)} tsp` });
        if (fromUnit !== "tbsp" && toUnit !== "tbsp") additionalInfo.push({ label: "بالملاعق الكبيرة", value: `${(inMlFinal / VOLUME_UNITS.tbsp.factor).toFixed(2)} tbsp` });
        if (fromUnit !== "cup" && toUnit !== "cup") additionalInfo.push({ label: "بالكوب الأمريكي", value: `${(inMlFinal / VOLUME_UNITS.cup.factor).toFixed(4)} cup` });
        if (fromUnit !== "fl_oz" && toUnit !== "fl_oz") additionalInfo.push({ label: "بالأونصة السائلة", value: `${(inMlFinal / VOLUME_UNITS.fl_oz.factor).toFixed(2)} fl oz` });

      } else if (conversionType === "weight_to_weight") {
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} جم/وحدة ÷ ${toFactor} جم/وحدة = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "بالجرام", value: `${inGrams.toFixed(2)} g` });
        if (fromUnit !== "kg" && toUnit !== "kg") additionalInfo.push({ label: "بالكيلوجرام", value: `${(inGrams / 1000).toFixed(4)} kg` });
        if (fromUnit !== "oz" && toUnit !== "oz") additionalInfo.push({ label: "بالأونصة", value: `${(inGrams / WEIGHT_UNITS.oz.factor).toFixed(2)} oz` });
        if (fromUnit !== "lb" && toUnit !== "lb") additionalInfo.push({ label: "بالرطل", value: `${(inGrams / WEIGHT_UNITS.lb.factor).toFixed(4)} lb` });

      } else if (conversionType === "volume_to_weight") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        const inGrams = inMl * density;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inMl.toFixed(2)} مل × ${density} جم/مل (كثافة ${ingName}) = ${inGrams.toFixed(2)} جم ÷ ${toFactor} = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "المكوّن", value: ingName });
        additionalInfo.push({ label: "الكثافة", value: `${density} جم/مل` });
        additionalInfo.push({ label: "الحجم بالمل", value: `${inMl.toFixed(2)} ml` });
        additionalInfo.push({ label: "بالجرام", value: `${inGrams.toFixed(2)} g` });
        additionalInfo.push({ label: "بالكيلوجرام", value: `${(inGrams / 1000).toFixed(4)} kg` });

      } else if (conversionType === "weight_to_volume") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        const inMl = inGrams / density;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inGrams.toFixed(2)} جم ÷ ${density} جم/مل (كثافة ${ingName}) = ${inMl.toFixed(2)} مل ÷ ${toFactor} = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "المكوّن", value: ingName });
        additionalInfo.push({ label: "الكثافة", value: `${density} جم/مل` });
        additionalInfo.push({ label: "بالمل", value: `${inMl.toFixed(2)} ml` });
        additionalInfo.push({ label: "بالملاعق الكبيرة", value: `${(inMl / VOLUME_UNITS.tbsp.factor).toFixed(2)} tbsp` });
        additionalInfo.push({ label: "بالكوب", value: `${(inMl / VOLUME_UNITS.cup.factor).toFixed(4)} cup` });
      }

      setResult({
        convertedValue,
        formulaText,
        fromUnitName: fromUnitName || fromUnit,
        toUnitName: toUnitName || toUnit,
        additionalInfo,
        inputValue: val,
      });
    } catch (err) {
      setError("خطأ في الحساب: " + err.message);
    }
  };

  useEffect(() => {
    if (inputValue) {
      calculateResult();
    } else {
      setResult(null);
      setError("");
    }
  }, [conversionType, fromUnit, toUnit, ingredient, inputValue]);

  // Reset units when conversion type changes
  useEffect(() => {
    if (conversionType === "volume_to_volume") {
      setFromUnit("cup");
      setToUnit("ml");
    } else if (conversionType === "weight_to_weight") {
      setFromUnit("g");
      setToUnit("oz");
    } else if (conversionType === "volume_to_weight") {
      setFromUnit("cup");
      setToUnit("g");
    } else if (conversionType === "weight_to_volume") {
      setFromUnit("g");
      setToUnit("ml");
    }
    setResult(null);
    setInputValue("");
    setError("");
  }, [conversionType]);

  const handleClear = () => {
    setInputValue("");
    setResult(null);
    setError("");
  };

  const isVolumeFrom =
    conversionType === "volume_to_volume" ||
    conversionType === "volume_to_weight";
  const isVolumeTo =
    conversionType === "volume_to_volume" ||
    conversionType === "weight_to_volume";
  const needsIngredient =
    conversionType === "volume_to_weight" ||
    conversionType === "weight_to_volume";

  const fromUnitsMap = isVolumeFrom ? VOLUME_UNITS : WEIGHT_UNITS;
  const toUnitsMap = isVolumeTo ? VOLUME_UNITS : WEIGHT_UNITS;

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>محول قياسات الطبخ</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>محول قياسات الطبخ</strong> أداة مجانية ودقيقة تساعدك
                على <strong>تحويل وحدات القياس</strong> بين الكوب والملعقة
                الكبيرة والصغيرة والمل والغرام والأوقية والرطل بسهولة. سواء
                كنت تتبع وصفة أمريكية أو أوروبية، احصل على التحويل الدقيق
                فوراً. مثالية للطبخ والخبيز وتحضير الحلويات والمشروبات.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  حوّل قياسات الطبخ بسهولة
                </h2>
              </div>

              {/* Conversion Type Selection */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="conversionType" >
                    نوع التحويل
                  </label>
                  <select
                    id="conversionType"
                    className="form-select"
                    value={conversionType}
                    onChange={(e) => setConversionType(e.target.value)}
                  >
                    {Object.entries(CONVERSION_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Ingredient Selection (only for volume ↔ weight) */}
              {needsIngredient && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="ingredient" >
                      المكوّن
                    </label>
                    <select
                      id="ingredient"
                      className="form-select"
                      value={ingredient}
                      onChange={(e) => setIngredient(e.target.value)}
                    >
                      {Object.entries(INGREDIENTS).map(([key, { name }]) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* From / To Units */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="fromUnit" >
                    من وحدة
                  </label>
                  <select
                    id="fromUnit"
                    className="form-select"
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                  >
                    {Object.entries(fromUnitsMap).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="toUnit" >
                    إلى وحدة
                  </label>
                  <select
                    id="toUnit"
                    className="form-select"
                    value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}
                  >
                    {Object.entries(toUnitsMap).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Input Value */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="inputValue" >
                    الكمية
                  </label>
                  <input
                    id="inputValue"
                    type="number"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    min="0"
                    step="0.001"
                    placeholder="أدخل الكمية"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  امسح
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            البيان
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            الكمية المُدخلة
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "16px",
                            }}
                          >
                            {result.inputValue} {result.fromUnitName}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            القيمة المحوّلة
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "20px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {result.convertedValue % 1 === 0
                              ? result.convertedValue.toFixed(0)
                              : result.convertedValue < 0.01
                              ? result.convertedValue.toFixed(6)
                              : result.convertedValue.toFixed(4)}{" "}
                            {result.toUnitName}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تحويلات سريعة إضافية</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          {result.additionalInfo.map((item, index) => (
                            <li key={index}>
                              🔄 <strong>{item.label}:</strong>{" "}
                              {item.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>نوع التحويل:</strong>{" "}
                            {CONVERSION_TYPES[conversionType]}
                          </p>
                          <p>
                            <strong>من:</strong> {result.fromUnitName}
                          </p>
                          <p>
                            <strong>إلى:</strong> {result.toUnitName}
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>طريقة الحساب:</strong>
                          </p>
                          <p style={{ fontSize: "13px", color: "#555", wordBreak: "break-word" }}>
                            {result.formulaText}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هو محول قياسات الطبخ؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>محول قياسات الطبخ</strong> أداة إلكترونية مجانية
                  تُريحك من متاعب التحويل اليدوي بين وحدات القياس المختلفة في
                  المطبخ. ما إن تفتح وصفة أجنبية حتى تجد نفسك أمام أكواب
                  وأونصات وبايتنات — المحوّل يحلّ هذه المشكلة في ثوانٍ.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  تدعم الأداة أربعة أنواع من التحويل:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حجم إلى حجم:</strong> بين الكوب والملعقة والمل
                    والأونصة السائلة والباينت والكوارت والجالون واللتر
                  </li>
                  <li>
                    <strong>وزن إلى وزن:</strong> بين الجرام والكيلوجرام
                    والأونصة والرطل
                  </li>
                  <li>
                    <strong>حجم إلى وزن:</strong> تحويل الكوب أو الملعقة إلى
                    جرامات أو رطل بحسب المكوّن
                  </li>
                  <li>
                    <strong>وزن إلى حجم:</strong> تحويل الجرامات إلى ملاعق
                    أو أكواب بحسب كثافة المكوّن
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام المحوّل:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر نوع التحويل المطلوب</li>
                  <li>
                    إذا كنت تحوّل بين حجم ووزن، اختر المكوّن من القائمة
                  </li>
                  <li>اختر وحدة البداية ووحدة الهدف</li>
                  <li>أدخل الكمية وستظهر النتيجة فوراً</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 ملاحظة:</strong> تحويل الحجم إلى وزن يعتمد على
                  كثافة المكوّن، لذا اختر المكوّن الصحيح للحصول على نتيجة
                  دقيقة. كوب الدقيق لا يساوي كوب السكر بالوزن!
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات محول قياسات الطبخ</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  للطبخ اليومي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>اتباع الوصفات الأجنبية:</strong> تحويل الأكواب
                    الأمريكية إلى وحدات معروفة محلياً
                  </li>
                  <li>
                    <strong>تعديل كميات الوصفة:</strong> تكبير أو تصغير
                    الوصفة مع الحفاظ على النسب
                  </li>
                  <li>
                    <strong>استبدال أدوات القياس:</strong> لا ميزان؟ حوّل
                    الجرامات إلى ملاعق وأكواب
                  </li>
                  <li>
                    <strong>الطبخ الصحي:</strong> قياس الكميات بدقة لحساب
                    السعرات
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للخبيز والحلويات:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الدقيق والسكر:</strong> الوصفات تتطلب دقة عالية
                    والتحويل يضمن النسب الصحيحة
                  </li>
                  <li>
                    <strong>المكوّنات السائلة:</strong> تحويل مل الحليب
                    والزيت والكريمة بين الوحدات
                  </li>
                  <li>
                    <strong>مسحوق الخبز والصودا:</strong> كميات صغيرة تحتاج
                    دقة في التحويل
                  </li>
                  <li>
                    <strong>الشوكولاتة والكاكاو:</strong> تحويل وزن إلى حجم
                    بحسب الكثافة
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للمشروبات والعصائر:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تحضير العصائر:</strong> تحويل أكواب إلى مل أو
                    لتر بدقة
                  </li>
                  <li>
                    <strong>قهوة وشاي:</strong> الحصول على النسبة المثالية
                    بين الماء والمكوّنات
                  </li>
                  <li>
                    <strong>الكوكتيلات:</strong> تحويل أوقيات سائلة إلى مل
                    للتحضير الدقيق
                  </li>
                  <li>
                    <strong>مشروبات اللياقة:</strong> قياس مساحيق البروتين
                    وتحويلها بين الوحدات
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  لمحترفي الطهي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>المطاعم والمقاهي:</strong> تحويل وصفات الكميات
                    الكبيرة بسرعة
                  </li>
                  <li>
                    <strong>التدريب المهني:</strong> تعلم التحويل بين أنظمة
                    القياس المختلفة
                  </li>
                  <li>
                    <strong>توحيد الوصفات:</strong> تحويل وحدات وصفة إلى نظام
                    قياسي موحد
                  </li>
                  <li>
                    <strong>إدارة المخزون:</strong> حساب الكميات المطلوبة
                    من المواد الخام
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>جدول وحدات قياسات الطبخ الشائعة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  إليك جدول مرجعي سريع بأهم التحويلات بين وحدات الحجم في
                  المطبخ. احفظه للرجوع إليه عند الحاجة:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        الوحدة
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        بالمل
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        تساوي
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>ملعقة صغيرة (tsp)</td>
                      <td style={{ textAlign: "center" }}>4.93 ml</td>
                      <td style={{ textAlign: "center" }}>⅓ ملعقة كبيرة</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>ملعقة كبيرة (tbsp)</td>
                      <td style={{ textAlign: "center" }}>14.79 ml</td>
                      <td style={{ textAlign: "center" }}>3 ملاعق صغيرة</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>أونصة سائلة (fl oz)</td>
                      <td style={{ textAlign: "center" }}>29.57 ml</td>
                      <td style={{ textAlign: "center" }}>2 ملعقة كبيرة</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>كوب أمريكي (cup)</td>
                      <td style={{ textAlign: "center" }}>236.59 ml</td>
                      <td style={{ textAlign: "center" }}>16 ملعقة كبيرة</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>باينت (pint)</td>
                      <td style={{ textAlign: "center" }}>473.18 ml</td>
                      <td style={{ textAlign: "center" }}>2 كوب</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>كوارت (quart)</td>
                      <td style={{ textAlign: "center" }}>946.35 ml</td>
                      <td style={{ textAlign: "center" }}>4 أكواب</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>جالون (gallon)</td>
                      <td style={{ textAlign: "center" }}>3785.41 ml</td>
                      <td style={{ textAlign: "center" }}>16 كوب</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>لتر (L)</td>
                      <td style={{ textAlign: "center" }}>1000 ml</td>
                      <td style={{ textAlign: "center" }}>4.23 كوب</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كثافات المكوّنات الشائعة:
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  لتحويل الحجم إلى وزن، نحتاج معرفة كثافة المكوّن. إليك
                  بعض القيم المرجعية:
                </p>
                <table className="table bmi-table" style={{ marginTop: "0.5rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المكوّن</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>الكثافة (جم/مل)</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>كوب واحد (جم)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>دقيق أبيض</td>
                      <td style={{ textAlign: "center" }}>0.529</td>
                      <td style={{ textAlign: "center" }}>125 جم</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>سكر أبيض</td>
                      <td style={{ textAlign: "center" }}>0.845</td>
                      <td style={{ textAlign: "center" }}>200 جم</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>زبدة</td>
                      <td style={{ textAlign: "center" }}>0.911</td>
                      <td style={{ textAlign: "center" }}>215 جم</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>ماء / حليب</td>
                      <td style={{ textAlign: "center" }}>1.0 / 1.03</td>
                      <td style={{ textAlign: "center" }}>237 / 244 جم</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>عسل</td>
                      <td style={{ textAlign: "center" }}>1.42</td>
                      <td style={{ textAlign: "center" }}>336 جم</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ فائدة:</strong> الميزان الرقمي أدق من الأكواب في
                  الخبيز. فرق بسيط في وزن الدقيق قد يغير ملمس العجين كلياً.
                  استخدم المحوّل للتحقق من الكميات قبل البدء.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على تحويل قياسات الطبخ</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: تحويل الكوب إلى مل للكيك
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> الوصفة تطلب 2.5 كوب حليب ولديك
                  قياس بالمل فقط.
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "تحويل حجم إلى حجم"</li>
                  <li>من: كوب أمريكي — إلى: مل</li>
                  <li>أدخل: 2.5</li>
                  <li>النتيجة: 591.47 ml</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب:</strong> 2.5 كوب × 236.588 مل/كوب = 591.47 ml
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 2: تحويل الدقيق من كوب إلى جرام
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> الوصفة تطلب 3 أكواب دقيق وتريد
                  القياس بالجرام على الميزان.
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "تحويل حجم إلى وزن"</li>
                  <li>اختر المكوّن: دقيق أبيض (للجميع)</li>
                  <li>من: كوب أمريكي — إلى: جرام</li>
                  <li>أدخل: 3</li>
                  <li>النتيجة: 375.57 جم</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 3: تحويل الأونصة إلى جرام للزبدة
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> الوصفة تطلب 4 oz زبدة، كم جراماً
                  هذا؟
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "تحويل وزن إلى وزن"</li>
                  <li>من: أونصة — إلى: جرام</li>
                  <li>أدخل: 4</li>
                  <li>النتيجة: 113.4 جم</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 4: تحويل السكر من جرام إلى ملاعق
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> تريد 50 جرام سكر أبيض بالملاعق
                  الكبيرة.
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "تحويل وزن إلى حجم"</li>
                  <li>اختر المكوّن: سكر أبيض</li>
                  <li>من: جرام — إلى: ملعقة كبيرة</li>
                  <li>أدخل: 50</li>
                  <li>النتيجة: حوالي 4 ملاعق كبيرة</li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح لاستخدام قياسات الطبخ بفعالية</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح عامة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>استخدم الميزان دائماً للخبيز:</strong> الأكواب
                    تعطي نتائج متفاوتة بحسب طريقة الملء والتعبئة
                  </li>
                  <li>
                    <strong>اختر المكوّن الصحيح:</strong> عند تحويل حجم إلى
                    وزن، كثافة كل مكوّن مختلفة تماماً
                  </li>
                  <li>
                    <strong>انتبه لنوع الكوب:</strong> الكوب الأمريكي 237 مل،
                    والكوب الأسترالي 250 مل — المحوّل يستخدم المقياس الأمريكي
                  </li>
                  <li>
                    <strong>المواد الجافة:</strong> مررها بالشوكة قبل القياس
                    بالكوب لتفادي التكتل والضغط الزائد
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  أخطاء شائعة يجب تجنبها:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>الخلط بين الأونصة الوزنية (oz) والأونصة السائلة (fl oz)</li>
                  <li>افتراض أن كوب الدقيق يساوي كوب السكر بالوزن</li>
                  <li>ملء الكوب بالضغط بدل التسوية لتحقيق قياس موحد</li>
                  <li>
                    نسيان تحويل الوحدة الكاملة (مثلاً: الباينت = 2 كوب وليس
                    1)
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للاستخدام الاحترافي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    احفظ تحويلات الوصفات المتكررة في ورقة منفصلة لتوفير
                    الوقت
                  </li>
                  <li>
                    عند مضاعفة الوصفة، تأكد أن نسب المكوّنات الجافة تبقى
                    صحيحة
                  </li>
                  <li>
                    للمطاعم: حوّل وحدات الوصفات إلى كيلوجرامات ولترات للسهولة
                  </li>
                  <li>
                    تحقق دائماً من الكثافة الفعلية للمكوّن إذا كانت الدقة
                    حرجة
                  </li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تنبيه:</strong> قيم الكثافة في المحوّل هي
                  متوسطات قياسية. قد تختلف قيمة مكوّن بعينه بحسب العلامة
                  التجارية أو طريقة التحضير. في الخبيز المهني، استخدم الميزان
                  دائماً للدقة القصوى.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كم ملعقة كبيرة في الكوب الواحد؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الكوب الأمريكي القياسي يساوي <strong>16 ملعقة كبيرة</strong>.
                  هذا يعني أن ربع الكوب = 4 ملاعق، ونصف الكوب = 8 ملاعق. رقم
                  سهل الحفظ يوفر عليك الكثير وقت تحضير الوصفات.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>ملعقة صغيرة (tsp):</strong> ثلاث ملاعق صغيرة تساوي
                  ملعقة كبيرة واحدة. أي كوب واحد = 48 ملعقة صغيرة.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كم مل في الكوب الأمريكي؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الكوب الأمريكي القياسي يساوي <strong>236.588 مل</strong>،
                  ويُقرَّب في الاستخدام اليومي إلى 240 مل. لكن انتبه: الكوب
                  الأسترالي = 250 مل، والكوب البريطاني القديم = 284 مل.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>مثال:</strong> إذا طلبت الوصفة 2 كوب ماء، فهذا يعني
                  حوالي 473 مل. أسهل من التفكير في كل كوب على حدة!
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف أحوّل كوب الدقيق إلى جرامات؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  كوب الدقيق الأبيض يساوي تقريباً <strong>125 جرام</strong>،
                  وهذا يعتمد على كثافة الدقيق (0.529 جم/مل). لكن ربما يختلف
                  قليلاً بحسب طريقة تعبئة الكوب.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  لتجنب هذا التفاوت، مرر الدقيق بالشوكة أولاً، ثم ضعه في
                  الكوب برفق وسوّ السطح بسكين. هكذا تحصل على القياس الموحد
                  الذي تفترضه معظم الوصفات الأمريكية.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما الفرق بين الأونصة السائلة والأونصة الوزنية؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  هذا من أكثر مصادر الارتباك في المطبخ. الأونصة السائلة
                  (fl oz) وحدة <strong>حجم</strong> تساوي 29.57 مل، وتُستخدم
                  لقياس السوائل. أما الأونصة الوزنية (oz) فهي وحدة{" "}
                  <strong>كتلة</strong> تساوي 28.35 جرام، وتُستخدم للمواد
                  الجافة والصلبة.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>مثال عملي:</strong> 8 fl oz ماء = 236 مل ≈ 236 جم.
                  لكن 8 oz دقيق ≈ 227 جم وهو ما يساوي تقريباً كوباً وثلاثة
                  أرباع.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف أحوّل وصفة للنصف أو الضعف؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  لتصغير وصفة للنصف، اقسم كل كمية على 2. لكن المشكلة تظهر
                  مع كميات مثل ¾ كوب — نصفها ⅜ كوب وهو رقم غير عملي. هنا
                  يفيد المحوّل: حوّل ¾ كوب إلى مل (177 مل)، النصف = 88.5 مل،
                  ثم حوّله إلى ملاعق (حوالي 6 ملاعق كبيرة).
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>للمضاعفة:</strong> اضرب الكمية في 2 ثم تحقق إذا كانت
                  النتيجة يمكن قياسها بأداة لديك. أحياناً أسهل تحويل 3 أكواب
                  إلى مل مباشرة وقياسها بكوب القياس.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل الميزان أدق من الأكواب في الخبيز؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>نعم بشكل قاطع.</strong> الأكواب تعتمد على الحجم
                  وهو متغير بحسب ضغط التعبئة وطريقة القياس. الميزان يعطيك
                  الوزن الفعلي بغض النظر عن أي عامل آخر. في الخبيز، فرق 10
                  جرامات في الدقيق قد يغير قوام العجين كلياً.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  ابدأ بالميزان للمكوّنات الحساسة (الدقيق، السكر، الزبدة)
                  واستخدم الملاعق للكميات الصغيرة جداً كالملح وباودر الخبز.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كم جراماً في ملعقة كبيرة من الزبدة؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  ملعقة كبيرة واحدة من الزبدة تساوي تقريباً{" "}
                  <strong>14 جراماً</strong>. الزبدة كثافتها 0.911 جم/مل،
                  وحجم الملعقة الكبيرة 14.79 مل، أي: 14.79 × 0.911 ≈ 13.5 جم.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  معظم أعواد الزبدة الأمريكية تحمل علامات تدل على الملاعق،
                  فكل علامة = ملعقة كبيرة = 14 جم. هذا يجعل القياس سهلاً جداً
                  بدون ميزان.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل المحوّل يعمل على الهاتف المحمول؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>بالتأكيد!</strong> المحوّل مصمم ليعمل بسلاسة على
                  جميع الأجهزة. يمكنك استخدامه في المطبخ مباشرة من هاتفك
                  أثناء الطبخ دون الحاجة لفتح الحاسوب.
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>📱 الهواتف الذكية:</strong> واجهة متجاوبة مريحة
                    للاستخدام بيد واحدة
                  </li>
                  <li>
                    <strong>📲 الأجهزة اللوحية:</strong> مساحة أكبر وعرض أوضح
                    للنتائج
                  </li>
                  <li>
                    <strong>💻 الحاسوب:</strong> تجربة كاملة مع كل المزايا
                  </li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  يعمل على أي متصفح حديث بدون تطبيقات إضافية، والنتائج تظهر
                  فوراً مع كل تغيير في المدخلات.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookingMeasurementCalculator;